// Enums aligned with backend models
export enum PreOrderAmountUnit {
  KG = "kg",
  TONS = "tons",
  LBS = "lbs",
  BAGS = "bags",
  CARTONS = "cartons",
  BOTTLES = "bottles",
  PALLETS = "pallets",
  CUBIC_YARDS = "cubic_yards",
  CUBIC_METERS = "cubic_meters",
  LITERS = "liters",
  GALLONS = "gallons",
}

export enum PreOrderStatus {
  PENDING = "pending",
  PARTIALLY_FULFILLED = "partially_fulfilled",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum MaterialTypeCategory {
  ORGANIC = "organic",
  CARBOARD = "cardboard",
  PLASTIC = "plastic",
  METAL = "metal",
  WOOD = "wood",
  SLUDGE = "sludge",
  MIXED = "mixed",
  CHEMICAL = "chemical",
  EWASTE = "e_waste",
  OIL = "oil",
  RUBBER = "rubber",
  TEXTILES = "textiles",
  GLASS = "glass",
  OTHER = "other",
}

export type PreOrderOrigin = 'recycler' | 'ops';

export interface PreOrderSchema {
  id: string;
  /** Who placed it: the recycler in the market app, or ops for a dispatched pickup. */
  origin?: PreOrderOrigin | string | null;
  material_category: string;
  material_id?: string | null;
  material_type_id?: string | null;
  waste_stream_profile_id?: string | null;
  regulatory_code_id?: string | null;
  material_name_snapshot?: string | null;
  category_name_snapshot?: string | null;
  regulatory_code_snapshot?: string | null;
  regulatory_code_description_snapshot?: string | null;
  mwan_source_category_id?: string | null;
  mwan_source_category_snapshot?: string | null;
  physical_state_snapshot?: string | null;
  amount: number;
  amount_unit: PreOrderAmountUnit | null;
  organization_id: string;
  data: Record<string, any>;
  status: PreOrderStatus;
  created_at: string;
  updated_at: string;
  amount_kg?: number;
  fulfilled_kg?: number;
  remaining_kg?: number;
  fulfillment_percent?: number;
  linked_pickup_count?: number;
  // Optional expanded relations if provided by API
  organization?: {
    id: string;
    name: string;
  } | null;
  material_type_ids?: string[];
}

export interface PreOrderLinkedPickup {
  pickup_order_id: string;
  status: string;
  amount: number;
  amount_unit: string;
  verified_weight_kg?: number | null;
  waste_outlet_id?: string | null;
  waste_outlet_name?: string | null;
  delivery_phase?: string | null;
  created_at: string;
}

export interface PreOrderDetail extends PreOrderSchema {
  amount_kg: number;
  fulfilled_kg: number;
  remaining_kg: number;
  fulfillment_percent: number;
  linked_pickups: PreOrderLinkedPickup[];
}

/** A pickup headed to this recycler that is not yet attached to a pre-order. */
export interface IncomingPickupOrder {
  id: string;
  amount: number;
  amount_unit: string | null;
  status: string;
  organization_id: string;
  material_id?: string | null;
  material_name_snapshot?: string | null;
  material_type: { id: string; name: string; parent_category: string; material_id?: string | null };
  data: Record<string, any>;
  created_at: string;
}

export interface IncomingPickupPagination {
  pickup_orders: IncomingPickupOrder[];
  total: number;
  page: number;
  limit: number;
}

export interface PreOrderRequest {
  material_category: string;
  material_id?: string;
  regulatory_code_id?: string;
  physical_state?: string;
  material_type_id?: string;
  waste_stream_profile_id?: string;
  organization_id: string;
  amount: number;
  amount_unit: PreOrderAmountUnit;
  data?: Record<string, any>;
}

export interface UpdatePreOrderRequest {
  material_category?: string;
  material_id?: string;
  material_type_id?: string;
  waste_stream_profile_id?: string;
  regulatory_code_id?: string;
  physical_state?: string;
  amount?: number;
  amount_unit?: PreOrderAmountUnit;
  data?: Record<string, any>;
}

export interface UpdatePreOrderStatusRequest {
  status: PreOrderStatus;
}

export interface PreOrderQueryParams {
  organization_id: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_pre_order?: 'asc' | 'desc';
  material_category?: string;
  status?: PreOrderStatus;
  start_date?: string;
  end_date?: string;
  search?: string;
}

export interface PreOrderPagination {
  pre_orders: PreOrderSchema[];
  total: number;
  page: number;
  limit: number;
} 
